import { useCallback, useEffect, useState } from 'react';
import type { GridSize, Table, Wall } from '../types';
import {
  bootstrapFromServer,
  deleteScenario as deleteScenarioRequest,
  loadActiveScenario,
  loadScenarioLibrary,
  loadScenarioSnapshot,
  persistScenarioAndQueue,
  refreshScenarioLibraryFromServer,
  setScenarioArchived,
  updateScenarioSettings as updateScenarioSettingsRequest,
  updateUserPreferences,
} from '../data/repository';
import type { LocalAuthUser, ScenarioSnapshot } from '../data/types';
import { useI18n } from '../i18n/I18nContext';
import { REALTIME_SCENARIO_LIBRARY_EVENT, REALTIME_SCENARIO_SNAPSHOT_EVENT, type RealtimeEventPayload } from '../realtime/events';

interface SaveScenarioOverrides {
  scenarioName?: string;
  gridSize?: GridSize;
  walls?: Wall[];
  tables?: Table[];
  showPrices?: boolean;
  itemizePrintItems?: boolean;
  status?: ScenarioSnapshot['status'];
  isDefault?: boolean;
}

interface SaveScenarioOptions {
  historyDescription?: string | null;
}

interface ScenarioPersistenceOptions {
  currentUser?: LocalAuthUser | null;
  scenarioName: string;
  loadedScenario: boolean;
  scenarioVersion: number | null;
  gridSize: GridSize;
  walls: Wall[];
  tables: Table[];
  showPrices: boolean;
  itemizePrintItems: boolean;
  setScenarioName: (value: string) => void;
  setLoadedScenario: (value: boolean) => void;
  setGridSize: (value: GridSize) => void;
  setWalls: (value: Wall[]) => void;
  setTables: (value: Table[]) => void;
  setShowPrices: (value: boolean) => void;
  setItemizePrintItems: (value: boolean) => void;
  showToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

export function useScenarioPersistence(options: ScenarioPersistenceOptions) {
  const { t } = useI18n();
  const {
    currentUser,
    scenarioName,
    loadedScenario,
    scenarioVersion,
    gridSize,
    walls,
    tables,
    showPrices,
    itemizePrintItems,
    setScenarioName,
    setLoadedScenario,
    setGridSize,
    setWalls,
    setTables,
    setShowPrices,
    setItemizePrintItems,
    showToast,
  } = options;

  const [scenarioNames, setScenarioNames] = useState<string[]>([]);
  const [archivedScenarioNames, setArchivedScenarioNames] = useState<string[]>([]);
  const [recentScenarioNames, setRecentScenarioNames] = useState<string[]>([]);

  const getPreferenceRecentScenarios = useCallback((): string[] => {
    const values = currentUser?.preferences?.recentScenarioNames;
    return Array.isArray(values) ? values.filter((value): value is string => typeof value === 'string') : [];
  }, [currentUser?.preferences?.recentScenarioNames]);

  const rememberRecentScenario = useCallback(async (name: string) => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return;
    }
    const storedRecents = recentScenarioNames.length > 0 ? recentScenarioNames : getPreferenceRecentScenarios();
    const nextRecents = [
      trimmedName,
      ...storedRecents.filter((entry) => entry !== trimmedName),
    ].slice(0, 10);
    setRecentScenarioNames(nextRecents);
    if (currentUser) {
      await updateUserPreferences({
        lastScenarioName: trimmedName,
        recentScenarioNames: nextRecents,
      });
    }
  }, [currentUser, getPreferenceRecentScenarios, recentScenarioNames]);

  const forgetRecentScenario = useCallback(async (name: string) => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return;
    }
    const storedRecents = recentScenarioNames.length > 0 ? recentScenarioNames : getPreferenceRecentScenarios();
    const nextRecents = storedRecents.filter((entry) => entry !== trimmedName);
    setRecentScenarioNames(nextRecents);
    if (currentUser) {
      await updateUserPreferences({
        lastScenarioName: null,
        recentScenarioNames: nextRecents,
      });
    }
  }, [currentUser, getPreferenceRecentScenarios, recentScenarioNames]);

  const refreshRecentScenarioNames = useCallback(async () => {
    setRecentScenarioNames(getPreferenceRecentScenarios());
  }, [getPreferenceRecentScenarios]);

  const refreshScenarioNames = useCallback(async () => {
    const [activeScenarios, archivedScenarios] = await Promise.all([
      loadScenarioLibrary('ACTIVE'),
      loadScenarioLibrary('ARCHIVED'),
    ]);
    setScenarioNames(activeScenarios.map((scenario) => scenario.name));
    setArchivedScenarioNames(archivedScenarios.map((scenario) => scenario.name));
  }, []);

  const refreshScenarioNamesFromServer = useCallback(async () => {
    await refreshScenarioLibraryFromServer();
    await refreshScenarioNames();
  }, [refreshScenarioNames]);

  useEffect(() => {
    void refreshScenarioNames();
  }, [refreshScenarioNames]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleRealtimeLibraryChange = (_event: Event) => {
      void refreshScenarioNamesFromServer().catch(() => undefined);
    };

    window.addEventListener(REALTIME_SCENARIO_LIBRARY_EVENT, handleRealtimeLibraryChange as EventListener);
    window.addEventListener(REALTIME_SCENARIO_SNAPSHOT_EVENT, handleRealtimeLibraryChange as EventListener);

    return () => {
      window.removeEventListener(REALTIME_SCENARIO_LIBRARY_EVENT, handleRealtimeLibraryChange as EventListener);
      window.removeEventListener(REALTIME_SCENARIO_SNAPSHOT_EVENT, handleRealtimeLibraryChange as EventListener);
    };
  }, [refreshScenarioNamesFromServer]);

  useEffect(() => {
    void refreshRecentScenarioNames();
  }, [refreshRecentScenarioNames]);

  const saveScenario = useCallback((silent: boolean = false, overrides?: SaveScenarioOverrides, saveOptions?: SaveScenarioOptions): void => {
    void (async () => {
      const nextScenarioName = overrides?.scenarioName ?? scenarioName;
      if (!nextScenarioName) {
        if (!silent) showToast(t('scenario.requireName'), 'error');
        return;
      }
      if (nextScenarioName.trim().toLowerCase().startsWith('lastscenario') || nextScenarioName.trim().toLowerCase().startsWith('defaultscenario')) {
        if (!silent) showToast(t('scenario.reservedName'), 'error');
        return;
      }

      const existing = await loadScenarioSnapshot(nextScenarioName);
      const knownCurrentVersion = Math.max(existing?.currentVersion ?? 0, scenarioVersion ?? 0) || undefined;
      const snapshot: ScenarioSnapshot = {
        scenarioId: existing?.scenarioId,
        scenarioName: nextScenarioName,
        gridSize: overrides?.gridSize ?? gridSize,
        walls: overrides?.walls ?? walls,
        tables: overrides?.tables ?? tables,
        showPrices: overrides?.showPrices ?? existing?.showPrices ?? showPrices,
        itemizePrintItems: overrides?.itemizePrintItems ?? existing?.itemizePrintItems ?? itemizePrintItems,
        status: overrides?.status ?? existing?.status ?? 'ACTIVE',
        isDefault: overrides?.isDefault ?? existing?.isDefault ?? false,
        currentVersion: knownCurrentVersion,
        updatedAt: new Date().toISOString(),
      };

      await persistScenarioAndQueue(snapshot, { historyDescription: saveOptions?.historyDescription ?? null });
      setLoadedScenario(true);
      await rememberRecentScenario(nextScenarioName);
      await refreshScenarioNames();
      if (!silent) showToast(t('scenario.saveSuccess'), 'success');
      })().catch((error) => {
        console.warn('Could not save scenario:', error);
        if (!silent) showToast(t('scenario.saveError'), 'error');
      });
  }, [
    gridSize,
    refreshScenarioNames,
    rememberRecentScenario,
    scenarioName,
    scenarioVersion,
    showPrices,
    itemizePrintItems,
    setLoadedScenario,
    showToast,
    tables,
    walls,
  ]);

  const loadScenarioByName = useCallback((name: string): void => {
    void (async () => {
      await bootstrapFromServer(name);
      await refreshScenarioLibraryFromServer();
      const data = await loadScenarioSnapshot(name);

        if (!data) {
          showToast(t('scenario.notFound'), 'error');
          return;
        }
        setScenarioName(data.scenarioName);
        setGridSize(data.gridSize);
        setWalls(data.walls);
        setTables(data.tables);
        setShowPrices(data.showPrices);
        setItemizePrintItems(data.itemizePrintItems);
        setLoadedScenario(true);
        await rememberRecentScenario(name);
        showToast(t('scenario.loadSuccess'), 'success');
      })().catch((err) => {
        console.warn('Could not parse saved scenario:', err);
        showToast(t('scenario.loadError'), 'error');
      });
  }, [
    rememberRecentScenario,
    setGridSize,
    setLoadedScenario,
    setScenarioName,
    setShowPrices,
    setItemizePrintItems,
    setTables,
    setWalls,
    showToast,
  ]);

  const deleteCurrentScenario = useCallback(async (): Promise<void> => {
    if (!loadedScenario || !scenarioName) {
      return;
    }

    try {
      await deleteScenarioRequest(scenarioName);
      setWalls([]);
      setTables([]);
      setScenarioName('');
      setLoadedScenario(false);
      await forgetRecentScenario(scenarioName);
      await refreshScenarioNames();
      showToast(t('scenario.deleteSuccess'), 'success');
    } catch (err) {
      console.warn('Could not delete scenario:', err);
      showToast(t('scenario.deleteError'), 'error');
      throw err;
    }
  }, [forgetRecentScenario, loadedScenario, refreshScenarioNames, scenarioName, setWalls, setTables, setScenarioName, setLoadedScenario, showToast, t]);

  const loadRecentScenario = useCallback((name: string): void => {
    loadScenarioByName(name);
  }, [loadScenarioByName]);

  const createScenario = useCallback((input: {
    nextName: string;
    gridCols: number;
    gridRows: number;
    isDefault: boolean;
    showPrices: boolean;
    itemizePrintItems: boolean;
  }): void => {
    void (async () => {
      const nextName = input.nextName.trim();
      if (!nextName) {
        showToast(t('scenario.requireNameCreate'), 'error');
        return;
      }
      if (nextName.toLowerCase().startsWith('lastscenario') || nextName.toLowerCase().startsWith('defaultscenario')) {
        showToast(t('scenario.reservedName'), 'error');
        return;
      }

      const snapshot: ScenarioSnapshot = {
        scenarioName: nextName,
        gridSize: { cols: input.gridCols, rows: input.gridRows },
        walls: [],
        tables: [],
        showPrices: input.showPrices,
        itemizePrintItems: input.itemizePrintItems,
        status: 'ACTIVE',
        isDefault: input.isDefault,
        updatedAt: new Date().toISOString(),
      };

      await persistScenarioAndQueue(snapshot);
      setScenarioName(nextName);
      setGridSize(snapshot.gridSize);
      setWalls([]);
      setTables([]);
      setShowPrices(snapshot.showPrices);
      setItemizePrintItems(snapshot.itemizePrintItems);
      setLoadedScenario(true);
      await rememberRecentScenario(nextName);
      await refreshScenarioNames();
      showToast(t('scenario.createSuccess'), 'success');
    })().catch((error) => {
      console.warn('Could not create scenario:', error);
      showToast(t('scenario.createError'), 'error');
    });
  }, [
    refreshScenarioNames,
    rememberRecentScenario,
    setItemizePrintItems,
    setShowPrices,
    setGridSize,
    setLoadedScenario,
    setScenarioName,
    setTables,
    setWalls,
    showToast,
  ]);

  const duplicateScenario = useCallback((input: {
    nextName: string;
    gridCols: number;
    gridRows: number;
    isDefault: boolean;
    showPrices: boolean;
    itemizePrintItems: boolean;
  }): void => {
    void (async () => {
      const nextName = input.nextName.trim();
      if (!nextName) {
        showToast(t('scenario.requireNameCreate'), 'error');
        return;
      }
      if (nextName.toLowerCase().startsWith('lastscenario') || nextName.toLowerCase().startsWith('defaultscenario')) {
        showToast(t('scenario.reservedName'), 'error');
        return;
      }

      const snapshot: ScenarioSnapshot = {
        scenarioName: nextName,
        gridSize: { cols: input.gridCols, rows: input.gridRows },
        walls: walls.map((wall) => ({ ...wall })),
        tables: tables.map((table) => ({
          ...table,
          products: Array.isArray(table.products) ? table.products.map((product) => ({ ...product })) : [],
        })),
        showPrices: input.showPrices,
        itemizePrintItems: input.itemizePrintItems,
        status: 'ACTIVE',
        isDefault: input.isDefault,
        updatedAt: new Date().toISOString(),
      };

      await persistScenarioAndQueue(snapshot);
      setScenarioName(nextName);
      setGridSize(snapshot.gridSize);
      setWalls(snapshot.walls);
      setTables(snapshot.tables);
      setShowPrices(snapshot.showPrices);
      setItemizePrintItems(snapshot.itemizePrintItems);
      setLoadedScenario(true);
      await rememberRecentScenario(nextName);
      await refreshScenarioNames();
      showToast(t('scenario.duplicateSuccess'), 'success');
    })().catch((error) => {
      console.warn('Could not duplicate scenario:', error);
      showToast(t('scenario.duplicateError'), 'error');
    });
  }, [
    refreshScenarioNames,
    rememberRecentScenario,
    setItemizePrintItems,
    setShowPrices,
    setGridSize,
    setLoadedScenario,
    setScenarioName,
    setTables,
    setWalls,
    showToast,
    tables,
    walls,
    t,
  ]);

  const setDefaultScenarioName = useCallback((name: string): void => {
    if (!name) return;
    void (async () => {
      const snapshot = await loadScenarioSnapshot(name);
      if (!snapshot) {
        showToast(t('scenario.notFoundLocal'), 'error');
        return;
      }
      await updateScenarioSettingsRequest({
        currentName: name,
        nextName: name,
        gridCols: snapshot.gridSize.cols,
        gridRows: snapshot.gridSize.rows,
        isDefault: true,
        showPrices: snapshot.showPrices,
        itemizePrintItems: snapshot.itemizePrintItems,
      });
      await refreshScenarioNames();
      showToast(t('scenario.setDefaultSuccess', { name }), 'success');
    })().catch(() => {
      showToast(t('scenario.setDefaultError'), 'error');
    });
  }, [refreshScenarioNames, showToast, t]);

  const tryAutoLoadScenario = useCallback((): void => {
    void loadActiveScenario().then((candidate) => {
      if (candidate) {
        setScenarioName(candidate.scenarioName);
        setGridSize(candidate.gridSize);
        setWalls(candidate.walls);
        setTables(candidate.tables);
        setShowPrices(candidate.showPrices);
        setItemizePrintItems(candidate.itemizePrintItems);
        setLoadedScenario(true);
        void rememberRecentScenario(candidate.scenarioName);
      }
    });
  }, [
    rememberRecentScenario,
    setItemizePrintItems,
    setGridSize,
    setLoadedScenario,
    setScenarioName,
    setShowPrices,
    setTables,
    setWalls,
  ]);

  const editScenario = useCallback((input: {
    currentName: string;
    nextName: string;
    gridCols: number;
    gridRows: number;
    isDefault: boolean;
    showPrices: boolean;
    itemizePrintItems: boolean;
  }): void => {
    void updateScenarioSettingsRequest(input)
      .then(async (updated) => {
        setScenarioName(updated.scenarioName);
        setGridSize(updated.gridSize);
        setWalls(updated.walls);
        setTables(updated.tables);
        setShowPrices(updated.showPrices);
        setItemizePrintItems(updated.itemizePrintItems);
        setLoadedScenario(true);
        await refreshScenarioNames();
        showToast(t('scenario.updateSuccess'), 'success');
      })
      .catch((error) => {
        console.warn('Could not update scenario:', error);
        showToast(t('scenario.updateError'), 'error');
      });
  }, [
    refreshScenarioNames,
    setItemizePrintItems,
    setGridSize,
    setLoadedScenario,
    setScenarioName,
    setShowPrices,
    setTables,
    setWalls,
    showToast,
  ]);

  const archiveCurrentScenario = useCallback((): void => {
    if (!loadedScenario || !scenarioName) {
      return;
    }

    void setScenarioArchived(scenarioName, true)
      .then(async () => {
        await refreshScenarioNames();
        await forgetRecentScenario(scenarioName);
        showToast(t('scenario.archiveSuccess'), 'success');
        setLoadedScenario(false);
        setScenarioName('');
        setTables([]);
        setWalls([]);
      })
      .catch((error) => {
        console.warn('Could not archive scenario:', error);
        showToast(t('scenario.archiveError'), 'error');
      });
  }, [forgetRecentScenario, loadedScenario, refreshScenarioNames, scenarioName, setLoadedScenario, setScenarioName, setTables, setWalls, showToast, t]);

  const restoreArchivedScenario = useCallback((name: string): void => {
    void setScenarioArchived(name, false)
      .then(async () => {
        await refreshScenarioNames();
        loadScenarioByName(name);
        showToast(t('scenario.restoreSuccess'), 'success');
      })
      .catch((error) => {
        console.warn('Could not restore scenario:', error);
        showToast(t('scenario.restoreError'), 'error');
      });
  }, [loadScenarioByName, refreshScenarioNames, showToast, t]);

  return {
    saveScenario,
    loadScenarioByName,
    deleteCurrentScenario,
    archiveCurrentScenario,
    restoreArchivedScenario,
    editScenario,
    createScenario,
    duplicateScenario,
    loadRecentScenario,
    setDefaultScenarioName,
    tryAutoLoadScenario,
    refreshScenarioNamesFromServer,
    scenarioNames,
    archivedScenarioNames,
    recentScenarioNames,
  };
}
